import React, { useState, useEffect, useLayoutEffect, useRef, useTransition } from 'react';
import { Location, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LinearProgress } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { getTransitionType } from '../../utils/routeTransitions';
import {
  tabVariants, tabTransition,
  pushVariants, pushTransition,
  popVariants, popTransition,
  expandVariants, expandTransition,
  replaceVariants, replaceTransition,
} from '../../utils/transitionVariants';

// Render-prop children: PageTransition passes the deferred location down so
// <Routes location={displayLocation}> renders the OLD route while the new
// lazy component loads. Scroll save/restore runs on displayLocation changes
// (not realLocation), so applying scroll never affects the still-visible
// outgoing page during the startTransition hold.
interface PageTransitionProps {
  children: (location: Location) => React.ReactNode;
}

type NavType = 'PUSH' | 'POP' | 'REPLACE';

// Tab-bar routes — these render inside <TabContainer> (a fixed-position
// overflow:auto element), which preserves its own scrollTop natively across
// display:none/block. PageTransition leaves their scroll alone.
const TAB_ROUTES = new Set([
  '/',
  '/search',
  '/trending',
  '/notifications',
  '/profile',
]);

// Window-scroll cache for non-tab pages (movie/person/watchlist detail, etc.).
// Survives mount/unmount of those pages so POP restores their scroll.
const scrollPositions = new Map<string, number>();

// Frame-poll cap for the synchronous restoration loop (~500ms at 60fps).
const MAX_RESTORE_FRAMES = 30;
// Outer time budget for the ResizeObserver-driven catch-up phase. Pages
// with heavy async data (watchlist, profile, movie detail) can grow over
// a second after mount; keep retrying until the document is tall enough.
const GROW_RESTORE_BUDGET_MS = 3000;

function pickVariants(navType: NavType, pathname: string) {
  if (navType === 'REPLACE') return { variants: replaceVariants, transition: replaceTransition };
  const type = getTransitionType(pathname);
  if (type === 'tab') return { variants: tabVariants, transition: tabTransition };
  if (type === 'expand') return { variants: expandVariants, transition: expandTransition };
  if (navType === 'POP') return { variants: popVariants, transition: popTransition };
  return { variants: pushVariants, transition: pushTransition };
}

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const realLocation = useLocation();
  const realNavType = useNavigationType() as NavType;
  const isNative = Capacitor.isNativePlatform();

  const [isPending, startTransitionFn] = useTransition();
  const [displayLocation, setDisplayLocation] = useState(realLocation);
  const [displayNavType, setDisplayNavType] = useState<NavType>(realNavType);

  useEffect(() => {
    if (realLocation.pathname === displayLocation.pathname) return;

    if (!isNative) {
      // On web: update immediately, no animation
      setDisplayLocation(realLocation);
      return;
    }

    const capturedNavType = realNavType;

    // POP — always immediate: the page is already in the module cache, and
    // iOS's swipe-back gesture moves the WKWebView layer synchronously.
    //
    // Tab PUSH — also immediate now that tabs are keep-alive: the route
    // returns null (no Suspense, nothing to defer), and the tab wrapper
    // becomes visible in the same React commit that changes realLocation.
    // Using startTransition here would delay displayLocation by a frame,
    // meaning the scroll-restore useLayoutEffect fires one frame after the
    // tab is already visible — causing the exact "flash to 0 then jump"
    // the user sees.
    if (capturedNavType === 'POP' || TAB_ROUTES.has(realLocation.pathname)) {
      setDisplayLocation(realLocation);
      setDisplayNavType(capturedNavType);
      return;
    }

    // Non-tab PUSH: startTransition defers the displayLocation update until
    // the lazy component finishes loading (Suspense resolves). While pending,
    // the old route keeps rendering — no ActionLoader flash.
    startTransitionFn(() => {
      setDisplayLocation(realLocation);
      setDisplayNavType(capturedNavType);
    });
  }, [realLocation.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Take control of scroll restoration from the browser on native — we
  // restore it ourselves, tied to displayLocation, below.
  useEffect(() => {
    if (!isNative) return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, [isNative]);

  // Scroll save / restore for NON-TAB pages only. Tab pages live inside
  // TabContainer (overflow:auto) and manage their own scroll natively —
  // touching window.scrollY for them would just disturb the underlying
  // non-tab page that the tab is overlaying.
  const prevDisplayPathRef = useRef(displayLocation.pathname);
  useLayoutEffect(() => {
    if (!isNative) return;

    const newPath = displayLocation.pathname;
    const prevPath = prevDisplayPathRef.current;
    if (prevPath === newPath) return;

    // Save the leaving page's window scroll, but only if it was a non-tab
    // page (tab pages don't scroll the window).
    if (!TAB_ROUTES.has(prevPath)) {
      scrollPositions.set(prevPath, window.scrollY);
    }
    prevDisplayPathRef.current = newPath;

    // Destination is a tab — TabContainer handles its scroll. Do nothing.
    if (TAB_ROUTES.has(newPath)) return;

    // PUSH/REPLACE to a non-tab page → always start at top.
    // POP back to a non-tab page → restore its saved scroll.
    // Both paths share the same retry loop because WKWebView can defer
    // window.scrollTo() inside useLayoutEffect — a one-shot scrollTo(0, 0)
    // on PUSH can leave the new page at the previous non-tab page's residual
    // window scroll (e.g., MovieDetail opening at WatchlistDetail's 500px).
    const savedY = displayNavType === 'POP'
      ? (scrollPositions.get(newPath) ?? 0)
      : 0;

    // Restore. Two phases that work together:
    //  1. Synchronous apply before first paint (useLayoutEffect) — eliminates
    //     the visible jump for pages that already have content in memory.
    //  2. Frame-poll for ~500ms — catches pages that need a few render cycles.
    //  3. ResizeObserver — for pages whose content grows after async data
    //     loads (watchlist movie posters, profile stats, etc.); re-applies
    //     the scroll the moment the document becomes tall enough.
    // Both use scrollTo(0, y) legacy form: some WKWebView builds silently
    // treat { behavior: 'instant' } as smooth. Cancels on user touch/wheel/
    // key/mouse so we never override a scroll the user is actively driving.
    let cancelled = false;
    let frames = 0;
    const apply = () => window.scrollTo(0, savedY);

    // Apply immediately before the browser paints (useLayoutEffect context).
    apply();

    // Use absolute difference so the loop fires for BOTH directions:
    // - scrolling DOWN to savedY (content still loading, scrollY < savedY)
    // - scrolling UP to savedY=0 (source tab left window at non-zero scrollY)
    const needsRetry = () => Math.abs(window.scrollY - savedY) > 5;

    const tryRestore = () => {
      if (cancelled) return;
      if (needsRetry()) {
        apply();
        if (frames++ < MAX_RESTORE_FRAMES) {
          requestAnimationFrame(tryRestore);
        }
      }
    };
    if (needsRetry()) {
      requestAnimationFrame(tryRestore);
    }

    const resizeObserver = new ResizeObserver(() => {
      if (cancelled) return;
      if (needsRetry()) apply();
    });
    resizeObserver.observe(document.documentElement);

    const onUserInteract = () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
    const interactionEvents: Array<keyof WindowEventMap> = [
      'touchstart',
      'wheel',
      'keydown',
      'mousedown',
    ];
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, onUserInteract, { passive: true, once: true })
    );

    const budgetTimer = window.setTimeout(() => {
      cancelled = true;
      resizeObserver.disconnect();
    }, GROW_RESTORE_BUDGET_MS);

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
      window.clearTimeout(budgetTimer);
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, onUserInteract)
      );
    };
  }, [displayLocation.pathname, displayNavType, isNative]);

  // Non-native: pass through with no animation wrapper
  if (!isNative) {
    return <>{children(realLocation)}</>;
  }

  const { variants, transition } = pickVariants(displayNavType, displayLocation.pathname);

  return (
    <>
      {/* Thin progress bar while lazy component is loading (isPending = true) */}
      {isPending && (
        <LinearProgress
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 2 }}
        />
      )}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={displayLocation.pathname}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={transition}
          style={{ width: '100%' }}
        >
          {children(displayLocation)}
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default PageTransition;
